// client/src/utils/imageHelper.js

/**
 * Image URL Helper - ImageKit Integration
 *
 * This helper centralizes all image URL generation in one place.
 * Now integrated with ImageKit for automatic image optimization and CDN delivery.
 *
 * ImageKit Integration Benefits:
 * - Automatic image optimization
 * - Multiple format delivery (WebP, AVIF)
 * - Responsive image sizing with transformations
 * - CDN delivery for faster loading worldwide
 * - Real-time image transformations
 */

// Configuration object for easy switching between environments
const IMAGE_CONFIG = {
    // Local development setup (fallback)
    LOCAL: {
        baseUrl: process.env.REACT_APP_IMAGE_BASE_URL || "http://localhost:3000",
        fallbackImage: "/placeholder-business.png",
    },

    // ImageKit configuration
    IMAGEKIT: {
        urlEndpoint: process.env.REACT_APP_IMAGEKIT_URL_ENDPOINT || "https://ik.imagekit.io/9awpwcdsev",
        publicKey: process.env.REACT_APP_IMAGEKIT_PUBLIC_KEY || "public_NWW2rDazKhaCz0sjell30k/YnKA=",
        imagekitId: process.env.REACT_APP_IMAGEKIT_ID || "9awpwcdsev",
        transformations: {
            thumbnail: `tr:w-150,h-150,fo-auto,q-${process.env.REACT_APP_IMAGE_QUALITY_THUMBNAIL || 70}`,
            card: `tr:w-300,h-300,fo-auto,q-${process.env.REACT_APP_IMAGE_QUALITY_CARD || 70}`,
            detail: `tr:w-800,h-800,fo-auto,q-${process.env.REACT_APP_IMAGE_QUALITY_DETAIL || 75}`,
            large: `tr:w-1200,h-1200,fo-auto,q-${process.env.REACT_APP_IMAGE_QUALITY_LARGE || 80}`
        },
        fallbackImage: "/placeholder-business.png"
    }
};

// Current environment - switch to IMAGEKIT to enable ImageKit CDN
const CURRENT_ENV = "IMAGEKIT";

/**
 * Generates the appropriate image URL based on current environment
 *
 * @param {string} imagePath - The image path from database (e.g., "/uploads/businesses/image.jpg" or "https://ik.imagekit.io/...")
 * @param {string} size - Image size variant ('thumbnail', 'card', 'detail', 'large')
 * @returns {string} - Complete image URL ready for use in <img> tags
 */
export const getImageUrl = (imagePath, size = "card") => {
    // Handle missing or empty image paths
    if (!imagePath || imagePath.trim() === "") {
        return getFallbackImageUrl();
    }

    // If imagePath is already a full ImageKit URL, add transformations if needed
    if (imagePath.startsWith("https://ik.imagekit.io/")) {
        // Check if URL already has transformations
        if (imagePath.includes("/tr:") || imagePath.includes("/tr=")) {
            return imagePath; // Already has transformations
        }

        // Add transformations to the URL
        const transformation = IMAGE_CONFIG.IMAGEKIT.transformations[size] || IMAGE_CONFIG.IMAGEKIT.transformations.card;

        // ImageKit transformation syntax: https://ik.imagekit.io/id/tr:w-300,h-200/path/to/image.jpg
        // Extract the parts: https://ik.imagekit.io/9awpwcdsev/businesses/image.jpg
        // Should become: https://ik.imagekit.io/9awpwcdsev/tr:w-300,h-200/businesses/image.jpg

        const urlParts = imagePath.split('/');
        // urlParts: ['https:', '', 'ik.imagekit.io', '9awpwcdsev', 'businesses', 'image.jpg']

        if (urlParts.length > 4) {
            // Insert transformation after the imagekit ID (index 3)
            urlParts.splice(4, 0, transformation);
            return urlParts.join('/');
        }

        // Fallback: return original URL
        return imagePath;
    }

    // ImageKit implementation
    if (CURRENT_ENV === "IMAGEKIT") {
        const transformation = IMAGE_CONFIG.IMAGEKIT.transformations[size] || IMAGE_CONFIG.IMAGEKIT.transformations.card;

        // Clean the image path (remove leading slash if present)
        const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;

        // Construct ImageKit URL with transformation
        return `${IMAGE_CONFIG.IMAGEKIT.urlEndpoint}/${transformation}/${cleanPath}`;
    }

    // Local development implementation
    if (CURRENT_ENV === "LOCAL") {
        return `${IMAGE_CONFIG.LOCAL.baseUrl}${imagePath}`;
    }

    // Fallback to local if environment not recognized
    return `${IMAGE_CONFIG.LOCAL.baseUrl}${imagePath}`;
};

/**
 * Returns the fallback/placeholder image URL
 * Used when business has no profile image
 */
export const getFallbackImageUrl = () => {
    if (CURRENT_ENV === "IMAGEKIT") {
        return IMAGE_CONFIG.IMAGEKIT.fallbackImage;
    }

    if (CURRENT_ENV === "LOCAL") {
        return IMAGE_CONFIG.LOCAL.fallbackImage;
    }

    return IMAGE_CONFIG.LOCAL.fallbackImage;
};

/**
 * Generates a placeholder based on business name initial
 * This creates a colored background with the first letter of business name
 *
 * @param {string} businessName - Name of the business
 * @returns {object} - Object with background color and letter for CSS styling
 */
export const getPlaceholderData = (businessName) => {
    if (!businessName) return { letter: "?", backgroundColor: "#6c757d" };

    const letter = businessName.charAt(0).toUpperCase();

    // Generate consistent color based on first letter
    // This ensures same business always gets same color
    const colors = [
        "#007bff",
        "#28a745",
        "#17a2b8",
        "#ffc107",
        "#dc3545",
        "#6f42c1",
        "#fd7e14",
        "#20c997",
    ];

    const colorIndex = letter.charCodeAt(0) % colors.length;

    return {
        letter,
        backgroundColor: colors[colorIndex],
    };
};

/**
 * Utility function to handle image loading errors
 * Call this in onError handlers to switch to fallback
 *
 * @param {Event} event - The error event from img onError
 * @param {string} businessName - Business name for placeholder generation
 */
export const handleImageError = (event, businessName = "") => {
    // Prevent infinite error loops
    if (event.target.src !== getFallbackImageUrl()) {
        event.target.src = getFallbackImageUrl();
    }

    // Add a data attribute to indicate this image failed
    // This can be useful for analytics or debugging
    event.target.setAttribute("data-image-failed", "true");

    console.warn(`Image failed to load for business: ${businessName}`);
};

/**
 * Get ImageKit configuration for use in components
 * Useful for IKImage and IKUpload components from imagekitio-react
 */
export const getImageKitConfig = () => {
    return {
        urlEndpoint: IMAGE_CONFIG.IMAGEKIT.urlEndpoint,
        publicKey: IMAGE_CONFIG.IMAGEKIT.publicKey,
        authenticationEndpoint: `${process.env.REACT_APP_API_URL || 'http://localhost:3000/api'}/imagekit/auth`
    };
};

/**
 * Switch between LOCAL and IMAGEKIT environments
 * Useful for testing or fallback scenarios
 */
export const switchEnvironment = (env) => {
    if (env === "LOCAL" || env === "IMAGEKIT") {
        console.log(`Switching image environment to: ${env}`);
        // Note: This would require making CURRENT_ENV mutable or using a state management solution
        // For now, change CURRENT_ENV constant directly in the code
    } else {
        console.warn(`Invalid environment: ${env}. Use "LOCAL" or "IMAGEKIT"`);
    }
};
